import React from 'react';

/**
 * Orientação de IA na tela de provedores (web). Duas partes montadas pelo AiProvidersManager:
 * - <AiGuidanceAlerts/> — alertas ativos ANTES da tabela: provedor PAGO no sinal de trade (com fix
 *   de 1 clique) e provedor MUDO há 24h (chave/modelo quebrado mascarado pelo fallback).
 * - <AiGuidancePanel/> — DEPOIS da tabela: papéis por finalidade (aiPurposeRouting) + card educativo.
 * Motivação: episódio 19/07 — Sonnet pago na análise de rotina custava ~25× o lucro mensal e um slug
 * free morto derrubava o primário em toda chamada, tudo em silêncio. Ver PLAYBOOK-RENTABILIDADE.md.
 */

function providerLabel(name, aiProviders) {
    const meta = aiProviders && aiProviders.find(p => p.name === name);
    return meta ? meta.label : name;
}

// Severidade e ícone passam a vir DO BACKEND (regras de coerência, 21/07): assim uma regra nova
// aparece na tela sem precisar de código por tipo aqui — era o que obrigava a mexer nos 2 clientes a
// cada aviso novo. Os tipos antigos, que não mandam esses campos, mantêm exatamente a aparência de
// antes pelo fallback abaixo.
const _ALERT_CLASS = { info: 'alert-info', warn: 'alert-warning', danger: 'alert-danger' };
function _alertClass(w) {
    if (w.severity && _ALERT_CLASS[w.severity]) return _ALERT_CLASS[w.severity];
    return w.type === 'PAID_IN_SIGNAL' ? 'alert-warning' : 'alert-danger';
}
function _icon(w) {
    if (w.icon) return w.icon;
    return w.type === 'PAID_IN_SIGNAL' ? '💸' : '🔇';
}

export function AiGuidanceAlerts({ guidance, onApplyRole, busy }) {
    if (!guidance || !guidance.warnings || !guidance.warnings.length) return null;
    return (
        <div className='mb-2'>
            {guidance.warnings.map((w, i) => (
                <div key={i} className={`alert py-2 mb-2 ${_alertClass(w)}`} style={{ fontSize: 13 }}>
                    <span>{_icon(w)} {w.text}</span>
                    {/* Ajuste sugerido que NÃO tem endpoint próprio (o controle vive nesta mesma
                        tela): aponta o número e o lugar, em vez de duplicar caminho de gravação. */}
                    {w.fix && w.fix.action === 'RAISE_AI_TOPN' && (
                        <span className='d-block mt-1 fw-bold' style={{ fontSize: 12 }}>
                            ➜ Sugerido: subir "Top-N do scanner" para {w.fix.suggested} ativos (logo abaixo, em Análise por IA).
                        </span>
                    )}
                    {w.type === 'PAID_IN_SIGNAL' && (
                        <button className='btn btn-sm btn-outline-dark ms-2 py-0' disabled={busy}
                            title='Tira este provedor do sinal de trade (continua ativo) e reserva ele pra reavaliação semanal + revisão de risco'
                            onClick={() => onApplyRole(w.providerId)}>
                            Aplicar papel recomendado
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}

export default function AiGuidancePanel({ guidance, list, aiProviders, onSetPurpose, busy }) {
    if (!guidance) return null;
    const activeProviders = (list || []).filter(p => p.isActive !== false);
    const poolNames = (guidance.providers || [])
        .filter(p => p.isActive && p.useForTradeSignal)
        .map(p => providerLabel(p.provider, aiProviders));

    return (
        <div className='mt-3'>
            <h6 className='mb-1'>🎯 Papéis da IA <small className='text-muted'>(quem faz o quê)</small></h6>
            <div className='mb-2' style={{ fontSize: 12 }}>
                <span className='text-muted'>Sinal de trade (análise a cada {guidance.aiSchedule && guidance.aiSchedule.startsWith('*/') ? guidance.aiSchedule.slice(2).split(' ')[0] + ' min' : 'ciclo'}, ~{guidance.signalPool ? guidance.signalPool.callsPerDay : '?'} chamadas/dia): </span>
                {poolNames.length
                    ? poolNames.map(n => <span key={n} className='badge bg-light text-dark border me-1'>{n}</span>)
                    : <span className='badge bg-danger'>nenhum provedor no sinal!</span>}
                <span className='text-muted ms-1'>— controlado pela opção “usar no sinal de trade” de cada provedor.</span>
            </div>
            <div className='table-responsive'>
                <table className='table table-sm align-middle mb-2' style={{ maxWidth: 720 }}>
                    <tbody>
                        {(guidance.purposes || []).map(p => (
                            <tr key={p.key}>
                                <td style={{ width: '45%' }}>
                                    <div style={{ fontSize: 13 }}>{p.label}</div>
                                    <small className='text-muted' style={{ fontSize: 11 }}>{p.hint}</small>
                                </td>
                                <td>
                                    <select className='form-select form-select-sm' style={{ maxWidth: 260 }} disabled={busy}
                                        value={p.providerId || 0}
                                        onChange={e => onSetPurpose(p.key, parseInt(e.target.value) || 0)}>
                                        <option value={0}>— cadeia padrão (primary → fallbacks) —</option>
                                        {activeProviders.map(pr => (
                                            <option key={pr.id} value={pr.id}>{providerLabel(pr.provider, aiProviders)}{pr.model ? ` · ${pr.model}` : ''}</option>
                                        ))}
                                    </select>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <details className='mb-2'>
                <summary style={{ fontSize: 13, cursor: 'pointer' }}>💡 Como escolher a IA de cada função (guia rápido)</summary>
                <div className='card card-body py-2 mt-1' style={{ fontSize: 12.5 }}>
                    <ul className='mb-1 ps-3'>
                        <li><strong>Free/barato no volume:</strong> o sinal de trade roda a cada poucos minutos — modelo caro aqui raramente se paga (o custo mensal projetado aparece na tabela acima).</li>
                        <li><strong>Pago nas decisões raras:</strong> reavaliação semanal e revisão de risco são poucas chamadas de alto impacto — é onde um modelo mais inteligente vale o preço.</li>
                        <li><strong>Backtest/sweep:</strong> pese num provedor free com cota folgada — é payload alto e tolera limite de taxa; não deixe no mesmo provedor que serve o sinal ao vivo.</li>
                        <li><strong>Modelos de “raciocínio” não servem pro sinal:</strong> a análise limita a resposta a poucos tokens e esses modelos gastam tudo pensando — a resposta vem vazia e cai no fallback.</li>
                        <li><strong>Provedor mudo = provedor quebrado:</strong> se um provedor ativo passa 24h sem servir nada, a falha está sendo mascarada pelo fallback — teste-o ou troque o modelo.</li>
                    </ul>
                    <small className='text-muted'>Custos exibidos são estimativas pela tabela pública de preços de cada provedor.</small>
                </div>
            </details>
        </div>
    );
}
